import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Collapse,
  Divider,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import { backtestConfigApi, backtestRunApi, executionApi } from '../../services/api';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';
import BacktestChartReviewPanel from './BacktestChartReviewPanel';
import type {
  AIBacktestConfig,
  AIBacktestConfigCreate,
  AIBacktestRun,
  AIBacktestRunDetail,
  AIBacktestTrade,
  AISystemExecutionListItem,
  BacktestTriggerMode,
  BacktestTimeframe,
} from '../../types';

interface BacktestPanelProps {
  systemId: number;
}

interface ConfigFormData {
  name: string;
  start_date: string;
  end_date: string;
  trigger_mode: BacktestTriggerMode;
  default_timeframe: BacktestTimeframe | null;
  initial_balance: number;
  risk_percent: number;
}

const TRIGGER_MODE_OPTIONS: { value: BacktestTriggerMode; label: string }[] = [
  { value: 'SCHEDULE', label: 'Follow AI System Schedules' },
  { value: 'CANDLE_CLOSE', label: 'On Every Candle Close' },
  { value: 'TICK', label: 'On Every Tick' },
];

const TIMEFRAME_OPTIONS: { value: BacktestTimeframe; label: string }[] = [
  { value: 'M1', label: '1 Minute' },
  { value: 'M5', label: '5 Minutes' },
  { value: 'M15', label: '15 Minutes' },
  { value: 'M30', label: '30 Minutes' },
  { value: 'H1', label: '1 Hour' },
  { value: 'H4', label: '4 Hours' },
  { value: 'D1', label: '1 Day' },
];

const shortExecutionId = (id?: string | null): string => {
  if (!id) return '-';
  return id.length > 12 ? `${id.slice(0, 8)}...${id.slice(-4)}` : id;
};

const ExecutionLinkChip: React.FC<{ id?: string | null }> = ({ id }) => {
  if (!id) {
    return <Typography variant="caption" color="text.secondary">-</Typography>;
  }

  return (
    <Chip
      label={shortExecutionId(id)}
      size="small"
      variant="outlined"
      component={RouterLink}
      to={`/ai/executions/${id}`}
      clickable
    />
  );
};

const normalizeStatus = (status: string): string => status.toUpperCase();

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
  switch (normalizeStatus(status)) {
    case 'COMPLETED':
    case 'SUCCESS':
      return 'success';
    case 'RUNNING':
      return 'primary';
    case 'PENDING':
    case 'START':
      return 'warning';
    case 'FAILED':
    case 'ERROR':
      return 'error';
    case 'CANCELLED':
    case 'STOPPED':
      return 'warning';
    default:
      return 'default';
  }
};

const isExecutionInProgress = (status: string): boolean =>
  ['START', 'PENDING', 'RUNNING'].includes(normalizeStatus(status));

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString();
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const formatDurationMs = (durationMs: number | null | undefined): string => {
  if (durationMs === null || durationMs === undefined || durationMs < 0) return '-';
  if (durationMs >= 60000) return `${(durationMs / 60000).toFixed(1)}m`;
  if (durationMs >= 1000) return `${(durationMs / 1000).toFixed(2)}s`;
  return `${durationMs.toFixed(0)}ms`;
};

const formatExecutionDuration = (execution: AISystemExecutionListItem): string => {
  if (execution.execution_time_ms !== null && execution.execution_time_ms !== undefined) {
    return formatDurationMs(execution.execution_time_ms);
  }
  if (!execution.completed_at) {
    return isExecutionInProgress(execution.status) ? 'In progress' : '-';
  }

  return formatDurationMs(
    new Date(execution.completed_at).getTime() - new Date(execution.created_at).getTime()
  );
};

const formatActionsSummary = (execution: AISystemExecutionListItem): string => {
  const summary = execution.actions_summary;
  if (!summary || summary.total === 0) return 'No actions';
  if (summary.failed > 0) {
    return `${summary.successful}/${summary.total} succeeded`;
  }
  return `${summary.total} completed`;
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(numericValue);
};

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const getOpenTradeCount = (run: AIBacktestRun): number => {
  const snapshotCount = Number(run.stats?.open_trades);
  if (Number.isFinite(snapshotCount)) return snapshotCount;
  return run.status === 'RUNNING'
    ? Math.max(run.total_trades - run.winning_trades - run.losing_trades, 0)
    : 0;
};

const getClosedTradeCount = (run: AIBacktestRun): number => {
  const snapshotCount = Number(run.stats?.closed_trades);
  if (Number.isFinite(snapshotCount)) return snapshotCount;
  return Math.max(run.total_trades - getOpenTradeCount(run), 0);
};

const getRunWinRate = (run: AIBacktestRun): number => {
  const snapshotRate = Number(run.stats?.win_rate);
  if (Number.isFinite(snapshotRate)) return snapshotRate;
  const closedTrades = getClosedTradeCount(run);
  return closedTrades > 0 ? (run.winning_trades / closedTrades) * 100 : 0;
};

const getRunReturnPercent = (run: AIBacktestRun): number | null => {
  if (
    run.initial_balance === null
    || run.initial_balance === undefined
    || run.total_profit_loss === null
    || run.total_profit_loss === undefined
  ) {
    return null;
  }
  const initialBalance = Number(run.initial_balance);
  const totalProfitLoss = Number(run.total_profit_loss);
  if (!Number.isFinite(initialBalance) || initialBalance === 0 || !Number.isFinite(totalProfitLoss)) {
    return null;
  }
  return (totalProfitLoss / initialBalance) * 100;
};

const getRealizedProfitLoss = (run: AIBacktestRun): number | null => {
  const rawSnapshotValue = run.stats?.realized_profit_loss;
  if (rawSnapshotValue !== null && rawSnapshotValue !== undefined) {
    const snapshotValue = Number(rawSnapshotValue);
    if (Number.isFinite(snapshotValue)) return snapshotValue;
  }
  return run.status === 'RUNNING' ? null : run.total_profit_loss;
};

const getUnrealizedProfitLoss = (run: AIBacktestRun): number | null => {
  const rawSnapshotValue = run.stats?.unrealized_profit_loss;
  if (rawSnapshotValue !== null && rawSnapshotValue !== undefined) {
    const snapshotValue = Number(rawSnapshotValue);
    if (Number.isFinite(snapshotValue)) return snapshotValue;
  }
  return run.status === 'RUNNING' ? null : 0;
};

export const BacktestPanel: React.FC<BacktestPanelProps> = ({ systemId }) => {
  const queryClient = useQueryClient();
  const backtestStartAccess = useActionAccess(AI_ACTION_SURFACES.systemBacktestStart);
  const [tabValue, setTabValue] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AIBacktestConfig | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<AIBacktestConfig | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [triggerModeFilter, setTriggerModeFilter] = useState<BacktestTriggerMode | ''>('');
  const [executionsPage, setExecutionsPage] = useState(0);
  const [executionsRowsPerPage, setExecutionsRowsPerPage] = useState(10);

  const [formData, setFormData] = useState<ConfigFormData>({
    name: '',
    start_date: '',
    end_date: '',
    trigger_mode: 'SCHEDULE',
    default_timeframe: null,
    initial_balance: 10000,
    risk_percent: 1.0,
  });

  // Fetch backtest configs for this AI system
  const { data: configs, isLoading: configsLoading } = useQuery({
    queryKey: ['backtest-configs', systemId],
    queryFn: () => backtestConfigApi.list({ ai_system: systemId }),
    enabled: !!systemId,
  });

  // Fetch backtest runs for this AI system
  const { data: runs, isLoading: runsLoading, refetch: refetchRuns } = useQuery({
    queryKey: ['backtest-runs', systemId, triggerModeFilter],
    queryFn: () => backtestRunApi.list({ 
      ai_system: systemId,
      ...(triggerModeFilter ? { trigger_mode: triggerModeFilter } : {}),
    }),
    enabled: !!systemId,
    refetchInterval: (query) => {
      // Auto-refresh every 3 seconds if there are running backtests
      const data = query.state.data;
      if (data?.results?.some((run: AIBacktestRun) => run.status === 'RUNNING')) {
        return 3000;
      }
      return false;
    },
  });

  // Fetch detailed run when expanded
  const { data: runDetail, isLoading: runDetailLoading, refetch: refetchRunDetail } = useQuery({
    queryKey: ['backtest-run-detail', selectedRunId],
    queryFn: () => backtestRunApi.get(selectedRunId!),
    enabled: !!selectedRunId,
    // Refetch when on Results tab and run is still running
    refetchInterval: (query) => {
      const data = query.state.data;
      if (tabValue === 2 && data?.status === 'RUNNING') {
        return 3000;
      }
      return false;
    },
  });

  // Fetch backtest-created AI system executions for this AI system
  const {
    data: backtestExecutions,
    isLoading: executionsLoading,
    isFetching: executionsFetching,
    refetch: refetchExecutions,
  } = useQuery({
    queryKey: ['backtest-executions', systemId, executionsPage, executionsRowsPerPage],
    queryFn: () =>
      executionApi.list({
        ai_system: systemId,
        execution_type: 'backtest',
        page: executionsPage + 1,
        page_size: executionsRowsPerPage,
        ordering: '-created_at',
      }),
    enabled: !!systemId && tabValue === 3,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (
        tabValue === 3 &&
        data?.results?.some((execution) => isExecutionInProgress(execution.status))
      ) {
        return 3000;
      }
      return false;
    },
  });

  // Create config mutation
  const createMutation = useMutation({
    mutationFn: (data: AIBacktestConfigCreate) => backtestConfigApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backtest-configs', systemId] });
      handleCloseForm();
      toast.success('Backtest configuration created');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to create configuration');
    },
  });

  // Update config mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AIBacktestConfigCreate> }) =>
      backtestConfigApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backtest-configs', systemId] });
      handleCloseForm();
      toast.success('Configuration updated');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to update configuration');
    },
  });

  // Delete config mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => backtestConfigApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backtest-configs', systemId] });
      queryClient.invalidateQueries({ queryKey: ['backtest-runs', systemId] });
      setDeleteConfirmOpen(false);
      setConfigToDelete(null);
      toast.success('Configuration deleted');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete configuration');
    },
  });

  // Start backtest mutation
  const startMutation = useMutation({
    mutationFn: (configId: string) => backtestRunApi.start(configId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['backtest-runs', systemId] });
      queryClient.invalidateQueries({ queryKey: ['backtest-executions', systemId] });
      // Set selected run to the new one and switch to Results tab
      if (data?.id) {
        setSelectedRunId(data.id);
        setTabValue(2);
      }
      toast.success('Backtest started');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to start backtest');
    },
  });

  // Stop backtest mutation
  const stopMutation = useMutation({
    mutationFn: (runId: string) => backtestRunApi.stop(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backtest-runs', systemId] });
      queryClient.invalidateQueries({ queryKey: ['backtest-executions', systemId] });
      // Invalidate run detail to get updated status
      if (selectedRunId) {
        queryClient.invalidateQueries({ queryKey: ['backtest-run-detail', selectedRunId] });
      }
      toast.success('Backtest stopped');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to stop backtest');
    },
  });

  // Delete run mutation
  const deleteRunMutation = useMutation({
    mutationFn: (runId: string) => backtestRunApi.delete(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backtest-runs', systemId] });
      queryClient.invalidateQueries({ queryKey: ['backtest-executions', systemId] });
      if (selectedRunId) setSelectedRunId(null);
      toast.success('Backtest run deleted');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete run');
    },
  });

  const handleOpenForm = (config?: AIBacktestConfig) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        name: config.name,
        start_date: config.start_date.slice(0, 16), // datetime-local format
        end_date: config.end_date.slice(0, 16),
        trigger_mode: config.trigger_mode,
        default_timeframe: config.default_timeframe,
        initial_balance: config.initial_balance,
        risk_percent: config.risk_percent,
      });
    } else {
      setEditingConfig(null);
      setFormData({
        name: '',
        start_date: '',
        end_date: '',
        trigger_mode: 'SCHEDULE',
        default_timeframe: null,
        initial_balance: 10000,
        risk_percent: 1.0,
      });
    }
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingConfig(null);
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error('Start and end dates are required');
      return;
    }

    const submitData: AIBacktestConfigCreate = {
      name: formData.name,
      ai_system: systemId,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: new Date(formData.end_date).toISOString(),
      trigger_mode: formData.trigger_mode,
      default_timeframe: formData.default_timeframe,
      initial_balance: formData.initial_balance,
      risk_percent: formData.risk_percent,
    };

    if (editingConfig) {
      updateMutation.mutate({ id: editingConfig.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDeleteClick = (config: AIBacktestConfig) => {
    setConfigToDelete(config);
    setDeleteConfirmOpen(true);
  };

  const handleStartBacktest = (configId: string) => {
    if (!backtestStartAccess.ensureAccess()) return;
    startMutation.mutate(configId);
  };

  const handleDeleteConfirm = () => {
    if (configToDelete) {
      deleteMutation.mutate(configToDelete.id);
    }
  };

  const renderConfigsTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Backtest Configurations</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
        >
          New Config
        </Button>
      </Box>

      {configsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : configs?.results?.length === 0 ? (
        <Alert severity="info">
          No backtest configurations yet. Create one to start backtesting your AI system.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Date Range</TableCell>
                <TableCell>Trigger Mode</TableCell>
                <TableCell>Initial Balance</TableCell>
                <TableCell align="center">Runs</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs?.results?.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>{config.name}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(config.start_date).toLocaleDateString()} -{' '}
                      {new Date(config.end_date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.trigger_mode_display}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{formatCurrency(config.initial_balance)}</TableCell>
                  <TableCell align="center">
                    <Chip label={config.runs_count} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleStartBacktest(config.id)}
                      disabled={startMutation.isPending || backtestStartAccess.disabled}
                      title={backtestStartAccess.disabled ? backtestStartAccess.blockedMessage : 'Start backtest'}
                    >
                      <PlayIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenForm(config)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(config)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  const renderRunsTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Backtest Runs</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Trigger Mode</InputLabel>
            <Select
              value={triggerModeFilter}
              label="Trigger Mode"
              onChange={(e) => setTriggerModeFilter(e.target.value as BacktestTriggerMode | '')}
            >
              <MenuItem value="">All Modes</MenuItem>
              {TRIGGER_MODE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={() => refetchRuns()}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {runsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : runs?.results?.length === 0 ? (
        <Alert severity="info">
          {triggerModeFilter 
            ? `No backtest runs with trigger mode "${TRIGGER_MODE_OPTIONS.find(o => o.value === triggerModeFilter)?.label}". Try a different filter or start a new backtest.`
            : 'No backtest runs yet. Start a backtest from the Configurations tab.'}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {runs?.results?.map((run) => (
            <Card key={run.id} variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {run.config_name}
                      </Typography>
                      <Chip
                        label={run.status_display}
                        size="small"
                        color={getStatusColor(run.status)}
                      />
                      <Chip
                        label={run.trigger_mode_display}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    </Box>

                    {run.status === 'RUNNING' && (
                      <Box mb={1}>
                        <LinearProgress
                          variant="determinate"
                          value={run.progress}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {run.progress}% complete
                        </Typography>
                      </Box>
                    )}

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Initial Balance
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(run.initial_balance)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {run.status === 'RUNNING' ? 'Current Equity' : 'Final Balance'}
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(run.final_balance)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Total P&L
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            (run.total_profit_loss ?? 0) >= 0
                              ? 'success.main'
                              : 'error.main'
                          }
                        >
                          {formatCurrency(run.total_profit_loss)}
                          {getRunReturnPercent(run) !== null && (
                            <Typography component="span" variant="caption" color="text.secondary" ml={0.75}>
                              ({formatPercent(getRunReturnPercent(run))})
                            </Typography>
                          )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Trades
                        </Typography>
                        <Typography variant="body2">
                          {run.total_trades} ({run.winning_trades}W / {run.losing_trades}L / {getOpenTradeCount(run)}O)
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Win Rate
                        </Typography>
                        <Typography variant="body2">
                          {getRunWinRate(run).toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Max Drawdown
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          {formatCurrency(run.max_drawdown)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box display="flex" gap={0.5}>
                    {run.status === 'RUNNING' && (
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => stopMutation.mutate(run.id)}
                        disabled={stopMutation.isPending}
                      >
                        <StopIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        setSelectedRunId(run.id);
                        setTabValue(2); // Switch to Results tab
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                    {run.status !== 'RUNNING' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteRunMutation.mutate(run.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Box mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    Started: {run.started_at ? new Date(run.started_at).toLocaleString() : '-'}
                    {run.completed_at && ` | Completed: ${new Date(run.completed_at).toLocaleString()}`}
                    {' | '}API Cost: ${run.total_cost.toFixed(4)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderResultsTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Detailed Results
      </Typography>

      {!selectedRunId ? (
        <Alert severity="info">
          Select a backtest run from the Runs tab to view detailed results.
        </Alert>
      ) : runDetailLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : runDetail ? (
        <Box>
          {/* Portfolio-wide summary. Symbol-specific metrics live in Chart Review below. */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                  <Typography variant="h6">Portfolio Summary</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Totals across every symbol in this backtest run.
                  </Typography>
                </Box>
                <Chip
                  label={runDetail.status_display}
                  size="small"
                  color={getStatusColor(runDetail.status)}
                />
              </Box>
              {runDetail.status === 'RUNNING' && (
                <Box mb={2}>
                  <LinearProgress
                    variant="determinate"
                    value={runDetail.progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {runDetail.progress}% complete
                  </Typography>
                </Box>
              )}
              <Grid container spacing={3}>
                <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                  <Typography variant="caption" color="text.secondary">Initial Balance</Typography>
                  <Typography variant="h6">{formatCurrency(runDetail.initial_balance)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {runDetail.status === 'RUNNING' ? 'Current Equity' : 'Final Balance'}
                  </Typography>
                  <Typography variant="h6">{formatCurrency(runDetail.final_balance)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                  <Typography variant="caption" color="text.secondary">Total P&L</Typography>
                  <Typography
                    variant="h6"
                    color={(runDetail.total_profit_loss ?? 0) >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(runDetail.total_profit_loss)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatPercent(getRunReturnPercent(runDetail))}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                  <Typography variant="caption" color="text.secondary">Realized P&L</Typography>
                  <Typography variant="h6">{formatCurrency(getRealizedProfitLoss(runDetail))}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                  <Typography variant="caption" color="text.secondary">Unrealized P&L</Typography>
                  <Typography variant="h6">{formatCurrency(getUnrealizedProfitLoss(runDetail))}</Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
                  <Typography variant="caption" color="text.secondary">Trades</Typography>
                  <Typography variant="h6">{runDetail.total_trades}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {runDetail.winning_trades}W / {runDetail.losing_trades}L / {getOpenTradeCount(runDetail)}O
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Started: {formatDateTime(runDetail.started_at)}
                </Typography>
                {runDetail.completed_at && (
                  <Typography variant="caption" color="text.secondary">
                    Completed: {formatDateTime(runDetail.completed_at)}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  API Cost: ${Number(runDetail.total_cost || 0).toFixed(4)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tokens: {formatNumber(runDetail.total_tokens)}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Statistics Summary */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Win Rate
                  </Typography>
                  <Typography variant="h6">
                    {getRunWinRate(runDetail).toFixed(1)}%
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Max Drawdown
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {formatCurrency(runDetail.max_drawdown)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Profit Factor
                  </Typography>
                  <Typography variant="h6">
                    {runDetail.stats?.profit_factor?.toFixed(2) || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Sharpe Ratio
                  </Typography>
                  <Typography variant="h6">
                    {runDetail.stats?.sharpe_ratio?.toFixed(2) || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <BacktestChartReviewPanel runDetail={runDetail} />

          {/* Trades Table */}
          <Typography variant="h6" gutterBottom>
            Trade History ({runDetail.trades?.length || 0} trades)
          </Typography>
          
          {runDetail.trades?.length === 0 ? (
            <Alert severity="info">No trades recorded in this backtest.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Symbol</TableCell>
                    <TableCell>Direction</TableCell>
                    <TableCell>Entry</TableCell>
                    <TableCell>Exit</TableCell>
                    <TableCell>Volume</TableCell>
                    <TableCell align="right">P&L</TableCell>
                    <TableCell>Exit Reason</TableCell>
                    <TableCell>Open Exec</TableCell>
                    <TableCell>Close Exec</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runDetail.trades?.map((trade) => (
                    <TableRow key={trade.id}>
                      <TableCell>{trade.trade_number}</TableCell>
                      <TableCell>{trade.symbol}</TableCell>
                      <TableCell>
                        <Chip
                          label={trade.direction}
                          size="small"
                          color={trade.direction === 'BUY' ? 'success' : 'error'}
                          icon={
                            trade.direction === 'BUY' ? (
                              <TrendingUpIcon />
                            ) : (
                              <TrendingDownIcon />
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {trade.entry_price} @ {new Date(trade.entry_timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {trade.exit_price ?? '-'}{' '}
                          {trade.exit_timestamp &&
                            `@ ${new Date(trade.exit_timestamp).toLocaleString()}`}
                        </Typography>
                      </TableCell>
                      <TableCell>{trade.volume}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color:
                            (trade.profit_loss ?? 0) >= 0
                              ? 'success.main'
                              : 'error.main',
                          fontWeight: 'bold',
                        }}
                      >
                        {formatCurrency(trade.profit_loss)}
                      </TableCell>
                      <TableCell>
                        <Chip label={trade.exit_reason} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <ExecutionLinkChip id={trade.ai_system_execution} />
                      </TableCell>
                      <TableCell>
                        <ExecutionLinkChip id={trade.closed_by_ai_system_execution} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ) : null}
    </Box>
  );

  const renderExecutionsTab = () => {
    const executions = backtestExecutions?.results ?? [];

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6">Backtest Executions</Typography>
            <Typography variant="body2" color="text.secondary">
              AI system execution records generated while running backtests.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {executionsFetching && !executionsLoading && <CircularProgress size={18} />}
            <IconButton onClick={() => refetchExecutions()}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {executionsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : executions.length === 0 ? (
          <Alert severity="info">
            No backtest executions found for this AI system yet. Start a backtest to generate execution records.
          </Alert>
        ) : (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Execution</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.map((execution) => (
                    <TableRow key={execution.id} hover>
                      <TableCell>
                        <ExecutionLinkChip id={execution.id} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={execution.status_display || execution.status}
                          size="small"
                          color={getStatusColor(execution.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDateTime(execution.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDateTime(execution.completed_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatExecutionDuration(execution)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            execution.actions_summary?.failed
                              ? 'warning.main'
                              : 'text.primary'
                          }
                        >
                          {formatActionsSummary(execution)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(execution.total_tokens)}</TableCell>
                      <TableCell align="right">{formatCurrency(execution.total_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={backtestExecutions?.count ?? 0}
              page={executionsPage}
              onPageChange={(_, page) => setExecutionsPage(page)}
              rowsPerPage={executionsRowsPerPage}
              onRowsPerPageChange={(event) => {
                setExecutionsRowsPerPage(parseInt(event.target.value, 10));
                setExecutionsPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </Paper>
        )}
      </Box>
    );
  };

  // Handle tab change with data refresh
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Refetch data when switching to Results tab
    if (newValue === 2 && selectedRunId) {
      refetchRunDetail();
    }
    // Refetch runs when switching to Runs tab
    if (newValue === 1) {
      refetchRuns();
    }
    // Refetch executions when switching to Executions tab
    if (newValue === 3) {
      refetchExecutions();
    }
  };

  return (
    <Box p={3}>
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Configurations" icon={<AssessmentIcon />} iconPosition="start" />
        <Tab
          label="Runs"
          icon={
            runs?.results?.some((r) => r.status === 'RUNNING') ? (
              <CircularProgress size={16} />
            ) : undefined
          }
          iconPosition="end"
        />
        <Tab label="Results" disabled={!selectedRunId} />
        <Tab label="Executions" />
      </Tabs>

      {tabValue === 0 && renderConfigsTab()}
      {tabValue === 1 && renderRunsTab()}
      {tabValue === 2 && renderResultsTab()}
      {tabValue === 3 && renderExecutionsTab()}

      {/* Config Form Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Backtest Configuration' : 'New Backtest Configuration'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Configuration Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Start Date"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="End Date"
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Trigger Mode</InputLabel>
              <Select
                value={formData.trigger_mode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    trigger_mode: e.target.value as BacktestTriggerMode,
                  })
                }
                label="Trigger Mode"
              >
                {TRIGGER_MODE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formData.trigger_mode === 'CANDLE_CLOSE' && (
              <FormControl fullWidth>
                <InputLabel>Default Timeframe</InputLabel>
                <Select
                  value={formData.default_timeframe || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      default_timeframe: (e.target.value || null) as BacktestTimeframe | null,
                    })
                  }
                  label="Default Timeframe"
                >
                  {TIMEFRAME_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Initial Balance"
                  type="number"
                  value={formData.initial_balance}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      initial_balance: parseFloat(e.target.value) || 0,
                    })
                  }
                  fullWidth
                  InputProps={{ inputProps: { min: 0, step: 100 } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Risk %"
                  type="number"
                  value={formData.risk_percent}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      risk_percent: parseFloat(e.target.value) || 0,
                    })
                  }
                  fullWidth
                  InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Saving...'
              : editingConfig
              ? 'Update'
              : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{configToDelete?.name}"? This will also delete all
            associated backtest runs.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BacktestPanel;
